/**
 * ParentDashboard — the landing page for parent accounts.
 *
 * Before this, parents were force-redirected from `home` straight to
 * the "My Children" portal, so they never saw a dashboard and never
 * saw parent-targeted announcements (which render on the dashboard).
 *
 * This screen gives parents:
 *   - A welcome header
 *   - Parent-targeted announcements (AnnouncementsBanner filters by
 *     role === 'parent')
 *   - A summary card per linked child, with a quick link into that
 *     child's detailed portal view
 *
 * It deliberately does NOT reuse the staff Dashboard, which is full of
 * staff stat cards and setup quick-actions irrelevant to parents.
 */
import { useEffect, useMemo } from 'react';
import { GraduationCap, ChevronRight, BookOpen, Calendar, FileText, MessageSquare } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { useParentAcademicHold, ParentHoldNotice } from '@/features/parent/academicHold';
import { useParentPortalControls, ParentNoticeBanner, ParentMaintenanceScreen } from '@/features/parent/parentPortalControls';
import ParentWelcomeModal from '@/features/parent/ParentWelcomeModal';
import PhotoNeededCard from '@/features/parent/PhotoNeededCard';
import { SCHOOL } from '@/lib/school.config';
import AnnouncementsBanner from '@/features/dashboard/AnnouncementsBanner';
import { SoftCard } from '@/features/finance/ui';
import StudentAvatar from '@/components/ui/StudentAvatar';
import type { TabId } from '@/components/Shell';

interface ParentDashboardProps {
  onNavigate: (tab: TabId, opts?: { studentId?: string }) => void;
}

export default function ParentDashboard({ onNavigate }: ParentDashboardProps) {
  const { students, classes } = useData();
  const { userData } = useAuth();
  const { heldFor, ready: holdReady } = useParentAcademicHold();
  const portal = useParentPortalControls();

  const linkedStudentIds = useMemo(
    () => userData?.parentAccount?.studentIds ?? [],
    [userData],
  );

  const linkedStudents = useMemo(
    () => students.filter((s) => linkedStudentIds.includes(s.id)),
    [students, linkedStudentIds],
  );

  /**
   * Open one child in the portal, optionally straight to a tab.
   *
   * The tab travels in a one-shot sessionStorage marker rather than the URL:
   * `navigate` only carries an entity id for a handful of deep-link routes,
   * and widening that for a parent-only convenience would change how every
   * staff route parses its hash. Same convention Shell already uses to hand
   * a flag to CommentReview.
   */
  function openChild(studentId: string, tab?: 'academic' | 'reports' | 'attendance' | 'profile') {
    try {
      sessionStorage.setItem(
        'lkc.parentPortalTarget',
        JSON.stringify({ studentId, ...(tab ? { tab } : {}) }),
      );
    } catch {
      /* private mode — the portal still opens, just on its default tab */
    }
    onNavigate('parentPortal', { studentId });
  }

  /* FROM THE PHOTO REMINDER EMAIL (Oct 2026). Its button opens the portal
     with ?open=photo: once the children have loaded, go straight to the
     upload for the first child the school still has no photo of. One-shot —
     the parameter is taken off the address so a refresh lands normally. */
  useEffect(() => {
    if (!linkedStudents.length) return;
    let wants = false;
    try { wants = new URLSearchParams(window.location.search).get('open') === 'photo'; } catch { /* ignore */ }
    if (!wants) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('open');
      window.history.replaceState(null, '', url.toString());
    } catch { /* ignore */ }
    const first = linkedStudents.find((s) => !s.photo?.path);
    if (first) openChild(first.id, 'profile');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedStudents]);

  function classNameFor(currentClass?: string) {
    if (!currentClass) return '—';
    const c = classes.find((cl) => cl.id === currentClass);
    return c ? c.name : currentClass;
  }

  // Full maintenance overrides everything (but never changes the arrears
  // hold itself — held families are simply shown maintenance until it lifts).
  if (portal.maintenance.enabled) {
    return <ParentMaintenanceScreen message={portal.maintenance.message} />;
  }
  if (!holdReady) {
    return <div className="py-16 text-center text-sm text-gray-500">Loading…</div>;
  }
  /* PER CHILD now, so the dashboard only stands down when EVERY linked child
     is paused. A parent with one child in arrears and one paid up still gets
     their dashboard — the portal gates the held child on its own tab. */
  const allChildrenHeld =
    linkedStudents.length > 0 && linkedStudents.every((s) => heldFor(s.id));
  if (allChildrenHeld) {
    /* The hold pauses marks, reports and attendance — not the photo or the
       contact details, which the school wants from every family (26 Oct
       2026). So the way to them stays on the screen above the notice. */
    return (
      <div className="space-y-5">
        <PhotoNeededCard students={linkedStudents} onAdd={(id) => openChild(id, 'profile')} />
        <ParentHoldNotice onGoToFees={() => onNavigate('parentFees')} />
        <SoftCard>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            While academic access is paused you can still add or change your child&apos;s photo and update their details.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {linkedStudents.map((s) => (
              <button
                key={s.id}
                onClick={() => openChild(s.id, 'profile')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {s.fullName}: photo &amp; details
              </button>
            ))}
          </div>
        </SoftCard>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* First-time welcome (shows once, persisted on the user doc) */}
      <ParentWelcomeModal />

      {portal.notice.enabled && (
        <div className="relative"><ParentNoticeBanner message={portal.notice.message} /></div>
      )}

      {/* Subtle gradient layer (matches staff dashboard aesthetic) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(27,58,107,0.06),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(212,169,44,0.05),transparent_45%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(45,82,149,0.18),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(212,169,44,0.08),transparent_45%)]"
      />

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-primary to-primaryDark text-white px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">
              Welcome, {userData?.displayName}
            </h1>
            <p className="text-white/50 text-sm mt-0.5">
              Parent Portal · {SCHOOL.platformName}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-5">
            <div className="text-center">
              <p className="text-lg font-bold leading-none">
                {linkedStudents.length}
              </p>
              <p className="text-[10px] text-white/40 mt-0.5">
                {linkedStudents.length === 1 ? 'Child' : 'Children'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Children the school has no photo of — straight to the upload (Oct 2026). */}
      <PhotoNeededCard students={linkedStudents} onAdd={(id) => openChild(id, 'profile')} />

      {/* Parent-targeted announcements */}
      <AnnouncementsBanner />

      {/* Children summary */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
          <GraduationCap size={16} /> My Children
        </h2>

        {linkedStudents.length === 0 ? (
          <SoftCard>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No children are linked to your account yet. Please contact the
              school office if you believe this is an error.
            </p>
          </SoftCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {linkedStudents.map((s) => (
              <SoftCard key={s.id} className="transition-shadow hover:shadow-md">
                <button
                  type="button"
                  onClick={() => openChild(s.id)}
                  className="w-full text-left group"
                >
                  <div className="flex items-center gap-4">
                    <StudentAvatar student={s} size="md" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {s.fullName}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Class {classNameFor(s.currentClass)}
                        {s.admissionNumber ? ` · ${s.admissionNumber}` : ''}
                      </p>
                    </div>
                    <ChevronRight
                      size={18}
                      className="text-slate-400 group-hover:text-primary shrink-0"
                    />
                  </div>
                </button>

                {/* These used to be decoration — three labels on a card that
                    only ever opened Academic. The school asked for the report
                    to be reachable from the first screen, and a parent who
                    signs in to read a report should not have to learn the
                    portal's tab bar to find it. */}
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <QuickLink icon={FileText} label="Reports" onClick={() => openChild(s.id, 'reports')} primary />
                  <QuickLink icon={BookOpen} label="Academic" onClick={() => openChild(s.id, 'academic')} />
                  <QuickLink icon={Calendar} label="Attendance" onClick={() => openChild(s.id, 'attendance')} />
                </div>
              </SoftCard>
            ))}
          </div>
        )}
      </div>
      {/* Quick actions */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
          <MessageSquare size={16} /> Quick actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => onNavigate('parentMessages')}
            className="text-left group"
          >
            <SoftCard className="transition-shadow group-hover:shadow-md">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <MessageSquare size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    Messages
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Contact the school, request consultations, query marks
                  </p>
                </div>
                <ChevronRight
                  size={18}
                  className="text-slate-400 group-hover:text-primary shrink-0"
                />
              </div>
            </SoftCard>
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickLink({
  icon: Icon,
  label,
  onClick,
  primary,
}: {
  icon: typeof BookOpen;
  label: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
        primary
          ? 'bg-primary text-white hover:bg-primaryDark'
          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
      }`}
    >
      <Icon size={12} /> {label}
    </button>
  );
}
